import { Autocomplete, Box, Button, CircularProgress, FormHelperText, Grid, TextField } from "@mui/material";
import Header from "components/Header";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'
import { BasePathProvider, useBasePath, useSession } from "components/Providers";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { getAdminStandards } from "store/lookup";
import { save as saveArtifact, } from "store/evaluation/admin-artifact";
import { enqueueSnackbar } from "notistack";
import _ from 'lodash'
import { FileUploadField } from "components/FileUploadField";

export default function ArtifactForm({ artifact }) {
    const basePath = useBasePath()
    const { id } = useParams()
    const adminStandards = useSelector(state => state.lookup.adminStandards)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { district } = useSession()

    useEffect(() => {
        adminStandards || dispatch(getAdminStandards())
    }, [Boolean(adminStandards), dispatch]);

    const indicators = adminStandards
        .filter(std => std.id <= 6)
        .flatMap(std =>
            std.adminEvaluationIndicators.map(ind => ({
                ...ind,
                standardName: std.name
            }))
        );

    const schema = yup.object().shape({})

    const { control, handleSubmit, formState, setError, clearErrors } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...artifact,
            indicatorIds: artifact?.indicatorIds?.map(ind => {
                const indicator = indicators.find(i => i.code === ind)
                return indicator ? { ...indicator, code: ind } : null
            }).filter(Boolean),
            fileId: artifact?.file || null
        }
    })

    const onSubmit = async (data) => {
        try {
            let fileId = artifact?.file?.id || null;

            if (data.fileId && (!artifact?.file || data.fileId.name !== artifact.file.name)) {
                const formData = new FormData();
                formData.append('afile', data.fileId);
                formData.append('districtId', district.id);

                const uploadRes = await fetch('/api/file', {
                    method: 'POST',
                    body: formData,
                });

                if (!uploadRes.ok) {
                    enqueueSnackbar(uploadRes.statusText || 'File upload failed', { variant: 'error' });
                    return;
                }

                const uploadData = await uploadRes.json();
                fileId = uploadData?.id;

                if (!fileId) {
                    enqueueSnackbar('Upload succeeded but fileId missing in response.', { variant: 'error' });
                    return;
                }
            }

            delete data.fileId;
            data.fileId = fileId;

            if (Array.isArray(data.indicatorIds)) {
                data.indicatorIds = data.indicatorIds.map(ind => ind.code);
            }

            let exceptions = ["file"]

            const res = await dispatch(saveArtifact(_.omit(data, exceptions)));
            if (res.payload) {
                navigate(basePath);
            }
        } catch (error) {
            enqueueSnackbar(error.message || 'Unexpected error during submission', { variant: 'error' });
            console.error('Submission error:', error);
        }
    };


    return (
        <BasePathProvider value={`${basePath}/evaluation`}>
            <Header title={id === 'new' ? 'Add Artifact' : 'Edit Artifact'} small close borderBottom />
            <Box data-component="ArtifactForm" component="form" onSubmit={handleSubmit(onSubmit)} py={2} px={1}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                        <Controller
                            name="indicatorIds"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    multiple
                                    options={indicators}
                                    getOptionLabel={(option) => `${option?.code} - ${option?.description?.slice(0, 50)}${option?.description?.length > 50 ? '…' : ''}`}
                                    groupBy={(option) => option?.standardName}
                                    onChange={(event, value) => field.onChange(value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Indicators"
                                            variant="outlined"
                                            error={!!formState.errors.indicatorList}
                                            helperText={formState.errors.indicatorList ? formState.errors.indicatorList.message : ''}
                                            size="small"
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="explanation"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Explanation"
                                    variant="outlined"
                                    error={!!formState.errors.notes}
                                    helperText={formState.errors.notes ? formState.errors.notes.message : ''}
                                    size="small"
                                    multiline
                                    rows={4}
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="fileId"
                            control={control}
                            rules={{ required: 'File is required' }}
                            render={({ field, fieldState }) => (
                                <>
                                    <FileUploadField
                                        value={field.value || null}
                                        onChange={(file) => field.onChange(file)}
                                        onError={(msg) => {
                                            if (msg) setError(field.name, { type: "manual", message: msg });
                                            else clearErrors(field.name);
                                        }}
                                    />

                                    {fieldState.error && (
                                        <FormHelperText error sx={{ ml: 1, mt: 0.5 }}>
                                            {fieldState.error.message}
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box textAlign="right" mt={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                type="submit"
                                disabled={formState.isSubmitting || !formState.isDirty}
                                startIcon={formState.isSubmitting && <CircularProgress size={20} />}
                            >
                                {formState.isSubmitting ? 'Saving...' : 'Save'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </BasePathProvider>
    )
}
